/**
 * @fileoverview The Today group's answer card (B60; owner, 2026-10-22: the loose lines under Today "need to look finished,
 * more like what's in Coming up"; Fable's direction A): the quiet day's answer, the postponed line, "All done for today."
 * as rows of one card in JobCard's own frame and tick-column grid, divided by hairlines,
 * instead of loose text on the page.
 *
 * Rows, in this order, each present only when the server has something to say:
 *   - The quiet day: a leaf in the garden green (outlined, with a light wash of the green inside: a bare 2px outline of the dark green read grey-green, C92), "Nothing needs you today." at the cards' title weight, and the brief's
 *     reason under it.
 *   - Postponed: the line and its Bring back / Show buttons (built by TodoGroups, which holds their state), a clock in the column.
 *   - All done: a check, "All done for today." in the garden green.
 * The "Water today?" answer is no longer a row here (B139: it has its own headline and card, WaterCard, first under
 * the weather card).
 * With no row the card is not drawn, so a busy day without them ends on its job cards, as Coming up does.
 *
 * Every word is the server's (the brief's todo and water); this only lays them out. Colours are theme colours.
 *
 * Where: TodoGroups, at the end of the Today group.
 * API: none.
 * Words: none of its own.
 *
 * @module QuietCard
 */

import React from 'react';
import { Check, Clock, Leaf } from 'lucide-react';
import { cn } from '@/lib/utils';

/** The card: the job cards' frame (JobCard's CARD), with the rows' hairlines between them (WaterCard borrows it). */
export const CARD = 'overflow-hidden rounded-xl border border-border bg-card';

/** One row's grid: the job card's tick column and text edge (JobCard), so the words line up with every card's (WaterCard borrows it). */
export const ROW = 'grid grid-cols-[2.75rem_minmax(0,1fr)] items-start gap-x-1.5 py-2.5 pl-1 pr-3';

/** What the card shows. */
export interface QuietCardProps {
  /** "Nothing needs you today." on a quiet day (the list's words.nothing); null when jobs are listed for today. */
  nothing?: string | null;
  /** Why nothing needs the gardener (the brief's quiet line), under the answer; null for none. */
  why?: string | null;
  /** "All done for today." once every job of today is ticked; null otherwise. */
  allDone?: string | null;
  /** The postponed jobs' lines and buttons, as TodoGroups builds them; null for none. */
  moved?: React.ReactNode;
}

/** One row of the card: its icon in the tick column, its words beside it. */
interface RowProps {
  /** The row's icon, drawn in the tick column. */
  icon: React.ReactNode;
  /** A hairline above it (every row but the first). */
  line: boolean;
  /** The row's words. */
  children: React.ReactNode;
}

/**
 * One row: the icon centred in a thumb-wide column (as the tick circle is), the words at the cards' text edge.
 *
 * @param props - RowProps.
 */
const Row: React.FC<RowProps> = ({ icon, line, children }) => (
  <div className={cn(ROW, line && 'border-t border-border')}>
    <span className="-my-1.5 flex h-11 w-11 items-center justify-center" aria-hidden="true">{icon}</span>
    <div className="min-w-0 pt-0.5">{children}</div>
  </div>
);

/**
 * The Today group's answer card, or nothing when it has no row.
 *
 * @param props - QuietCardProps.
 */
export const QuietCard: React.FC<QuietCardProps> = ({ nothing = null, why = null, allDone = null, moved = null }) => {
  const rows: React.ReactNode[] = [];
  /**
   * Adds a row, with a hairline above it unless it is the first.
   *
   * @param key - The row's key.
   * @param icon - Its icon.
   * @param body - Its words.
   */
  const add = (key: string, icon: React.ReactNode, body: React.ReactNode): void => {
    rows.push(<Row key={key} icon={icon} line={rows.length > 0}>{body}</Row>);
  };
  if (nothing) {
    add('nothing', <Leaf className="h-5 w-5 fill-verdict-good/30 text-verdict-good" />, (
      <>
        <p className="text-[1.0625rem] font-semibold leading-snug text-foreground">{nothing}</p>
        {why && <p className="mt-0.5 text-sm leading-snug text-muted-foreground">{why}</p>}
      </>
    ));
  }
  if (moved) add('moved', <Clock className="h-4 w-4 text-muted-foreground" />, moved);
  if (allDone) add('allDone', <Check className="h-5 w-5 text-verdict-good" strokeWidth={3} />, <p className="text-[0.9375rem] font-semibold leading-snug text-verdict-good">{allDone}</p>);
  if (rows.length === 0) return null;
  return <div className={cn(CARD, 'mt-2')}>{rows}</div>;
};
